import { useSyncExternalStore } from 'react';

export interface AuthUser {
  id: string;
  /** Null on a wallet-only account, which has an address instead. */
  email: string | null;
  walletAddress: string | null;
  role: string;
}

export interface AuthTenant {
  id: string;
  name: string;
}

export interface AuthSession {
  token: string;
  user: AuthUser;
  tenant: AuthTenant;
}

export interface AuthState {
  token: string | null;
  user: AuthUser | null;
  tenant: AuthTenant | null;
}

const STORAGE_KEY = 'legate.session';

const EMPTY: AuthState = { token: null, user: null, tenant: null };

function read(): AuthState {
  if (typeof localStorage === 'undefined') return EMPTY;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<AuthSession>;
    if (typeof parsed.token !== 'string' || parsed.token.length === 0) return EMPTY;
    return {
      token: parsed.token,
      user: parsed.user ?? null,
      tenant: parsed.tenant ?? null,
    };
  } catch {
    return EMPTY;
  }
}

let state: AuthState = read();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function persist(next: AuthState): void {
  state = next;
  if (typeof localStorage !== 'undefined') {
    try {
      if (next.token === null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage disabled — the session stays in memory for this tab */
    }
  }
  emit();
}

/**
 * The token store. Deliberately not a React context: `api.ts` reads the token
 * synchronously on every request and must not depend on the render tree.
 */
export const authStore = {
  getToken(): string | null {
    return state.token;
  },

  getState(): AuthState {
    return state;
  },

  setSession(session: AuthSession): void {
    persist({ token: session.token, user: session.user, tenant: session.tenant });
  },

  setToken(token: string | null): void {
    persist(token === null ? EMPTY : { ...state, token });
  },

  clear(): void {
    persist(EMPTY);
  },

  isAuthenticated(): boolean {
    return state.token !== null;
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

if (typeof window !== 'undefined') {
  // Signing out in one tab signs out the others.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    state = read();
    emit();
  });
}

/** React binding over the same store. Re-renders on sign-in and sign-out. */
export function useAuth(): AuthState {
  return useSyncExternalStore(authStore.subscribe, authStore.getState, () => EMPTY);
}

/**
 * Decodes the `exp` claim without verifying the signature — used only to avoid
 * sending a token the server will certainly reject. Verification is the API's job.
 */
export function isTokenExpired(token: string, skewSeconds = 30): boolean {
  const parts = token.split('.');
  const payload = parts[1];
  if (parts.length !== 3 || payload === undefined) return false;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { exp?: number };
    if (typeof claims.exp !== 'number') return false;
    return claims.exp * 1000 <= Date.now() + skewSeconds * 1000;
  } catch {
    return false;
  }
}
